<script lang="ts" setup>
import ToggleButton from "@/components/atoms/button/ToggleButton.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import QualificationImage from "@/components/molecules/other/QualificationImage.vue";
import UploadImageCautionArea from "@/components/molecules/other/UploadImageCautionArea.vue";
import UploadImageForm from "@/components/organisms/register/UploadImageForm.vue";
import { Qualification } from "@/types/Master";
import { UserQualificationInputValues } from "@/types/qualification/EditableUserQualificationItems";
import { UserQualification } from "@/types/qualification/UserQualification";
import type { PropType } from "vue";
import { computed, ref } from "vue";

const cautions = [
  "四隅が写真に収まっている",
  "印字内容が鮮明",
  "関係ないものが写っていない",
  "撮影するのは1枚の資格証のみ"
];

const props = defineProps({
  value: {
    type: Object as PropType<UserQualificationInputValues>,
    required: true
  },
  verifiedQualification: {
    type: Object as PropType<UserQualification | null>,
    default: null
  },
  qualificationOptions: {
    type: Array as PropType<Qualification[]>,
    default: () => []
  },
  isCameraActive: {
    type: Boolean,
    default: true
  },
  disabledQualificationId: {
    type: Boolean,
    default: false
  },
  disabledImage: {
    type: Boolean,
    default: false
  },
  disabledText: {
    type: String,
    default: ""
  }
});

const emits = defineEmits(["input", "update:isCameraActive"]);

const isShownVerifiedImage = ref<boolean>(false)

const image = computed(() => {
  if (isShownVerifiedImage.value && props.verifiedQualification) {
    return props.verifiedQualification.image;
  }
  return props.value.image
});
const options = computed(() => {
    return [
      "[資格証の全体]が入っていますか？",
      "反射なく、はっきり読み取れますか？"
    ];
});
const isCameraActiveValue = computed({
  get: () => props.isCameraActive,
  set: (v: boolean) => {
    emits("update:isCameraActive", v);
  }
});

function changeImage(image: string) {
  emits("input", {
    ...props.value,
    image
  });
  // 申請前の写真を表示していた場合、変更した画像を表示するためトグルを戻す
  isShownVerifiedImage.value = false;
}
function onToggle() {
  isShownVerifiedImage.value = !isShownVerifiedImage.value;
}
</script>

<template>
  <div>
    <UploadImageCautionArea
      v-if="!disabledImage"
      :cautions="cautions"
      class="mb-4"
    />
    <ValidationProvider
      v-slot="validationState"
      name="資格"
      tag="div"
      rules="required"
      vid="qualification"
    >
      <FormSelectGroup
        label="資格種別"
        appLabelText="必須"
        :value="value.qualification"
        :options="qualificationOptions"
        text-field="name"
        value-field="id"
        :validation-state="validationState"
        class="mb-4"
        :disabled="disabledQualificationId"
      />
    </ValidationProvider>
    <div v-if="verifiedQualification">
      <div class="d-flex align-items-center justify-content-end toggle">
        <span class="text-size-12 text-mild mr-2">申請前の写真を表示</span>
        <ToggleButton :checked="isShownVerifiedImage" @click="onToggle" />
      </div>
    </div>
    <UploadImageForm
      :value="image"
      photoGraphingType="qualification"
      appLabelText="必須"
      appLabelVariant="red"
      :isFrontCamera="false"
      v-model:isCameraActive="isCameraActiveValue"
      :options="options"
      :disabled="disabledImage"
      :disabledText="disabledText"
      @input="changeImage"
    >
      <QualificationImage :imageUrl="image" />
    </UploadImageForm>
  </div>
</template>
